/**
 * AdminDashboardPage - Main admin dashboard with overview metrics and charts.
 *
 * Displays:
 * - Key metrics (Total Users, Active Today, Retention, MRR)
 * - User growth chart
 * - Recent signups
 * - Subscription distribution pie chart
 * - Top disciplines
 */

import { useState } from "react";
import { useAdminDashboard, type TimeRange } from "../../hooks/useAdminDashboard";
import { MetricCard } from "../../components/admin/MetricCard";
import { SimpleLineChart } from "../../components/admin/SimpleLineChart";
import { SimplePieChart } from "../../components/admin/SimplePieChart";
import { RecentActivityList } from "../../components/admin/RecentActivityList";
import { TopDisciplines } from "../../components/admin/TopDisciplines";
import { TimeRangeSelector } from "../../components/admin/TimeRangeSelector";

export function AdminDashboardPage() {
  const [timeRange, setTimeRange] = useState<TimeRange>("24h");
  const { data, isLoading, error, isCached, refetch } = useAdminDashboard(timeRange);

  // Format MRR from cents to dollars
  const formatMRR = (cents: number) => {
    const dollars = cents / 100;
    if (dollars >= 1000) {
      return `$${(dollars / 1000).toFixed(1)}K`;
    }
    return `$${dollars.toFixed(0)}`;
  };

  // Calculate retention rate (simplified: active users / total users)
  const retentionRate = data
    ? Math.round((data.activeToday / Math.max(data.totalUsers, 1)) * 100)
    : 0;

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
          <p className="mt-1 text-sm text-gray-500">
            Overview of your JumpBean platform
            {isCached && (
              <span className="ml-2 text-xs text-gray-400">(cached)</span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <TimeRangeSelector value={timeRange} onChange={setTimeRange} />
          <button
            onClick={refetch}
            disabled={isLoading}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
            title="Refresh data"
          >
            <RefreshIcon className={`w-5 h-5 ${isLoading ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      {/* Error state */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <ErrorIcon className="w-5 h-5 text-red-500 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-red-800">
                Failed to load dashboard data
              </p>
              <p className="text-sm text-red-600 mt-1">{error}</p>
              <button
                onClick={refetch}
                className="mt-2 text-sm font-medium text-red-700 hover:text-red-800"
              >
                Try again
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Stats grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          title="Total Users"
          value={isLoading ? "—" : data?.totalUsers.toLocaleString() ?? "0"}
          change={data?.newUsersChange}
          changeLabel="today"
          icon={<UsersIcon />}
          loading={isLoading}
        />
        <MetricCard
          title="Active Today"
          value={isLoading ? "—" : data?.activeToday.toLocaleString() ?? "0"}
          change={data?.activeChange}
          icon={<ActivityIcon />}
          loading={isLoading}
        />
        <MetricCard
          title="Retention"
          value={isLoading ? "—" : `${retentionRate}%`}
          change={data?.activeChange}
          icon={<RetentionIcon />}
          loading={isLoading}
        />
        <MetricCard
          title="MRR"
          value={isLoading ? "—" : formatMRR(data?.mrr ?? 0)}
          change={data?.mrrChange}
          icon={<RevenueIcon />}
          loading={isLoading}
        />
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <SimpleLineChart
          data={data?.userGrowth ?? []}
          loading={isLoading}
          title={`User Growth (${timeRange === "24h" ? "Today" : timeRange === "7d" ? "7 Days" : "30 Days"})`}
        />
        <RecentActivityList
          signups={data?.recentSignups ?? []}
          loading={isLoading}
        />
      </div>

      {/* Bottom row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <SimplePieChart
          data={
            data?.subscriptionBreakdown ?? { free: 0, family: 0, classroom: 0 }
          }
          loading={isLoading}
        />
        <TopDisciplines
          disciplines={data?.topDisciplines ?? []}
          loading={isLoading}
        />
      </div>
    </div>
  );
}

// Icons
function UsersIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5">
      <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />
    </svg>
  );
}

function ActivityIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5">
      <path
        fillRule="evenodd"
        d="M3 6a3 3 0 013-3h10a1 1 0 01.8 1.6L14.25 8l2.55 3.4A1 1 0 0116 13H6a1 1 0 00-1 1v3a1 1 0 11-2 0V6z"
        clipRule="evenodd"
      />
    </svg>
  );
}

function RetentionIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5">
      <path
        fillRule="evenodd"
        d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z"
        clipRule="evenodd"
      />
    </svg>
  );
}

function RevenueIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5">
      <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z" />
      <path
        fillRule="evenodd"
        d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z"
        clipRule="evenodd"
      />
    </svg>
  );
}

function RefreshIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" className={className}>
      <path
        fillRule="evenodd"
        d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z"
        clipRule="evenodd"
      />
    </svg>
  );
}

function ErrorIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" className={className}>
      <path
        fillRule="evenodd"
        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
        clipRule="evenodd"
      />
    </svg>
  );
}

export default AdminDashboardPage;
